import React, { useMemo } from 'react';

import type { InvoiceConfigInfoInvoiceConfigSchema } from '~/server_shared/openapi/data-contracts';

import type { LineItemColumnId } from './columnUtils';
import type { InvoiceRecommendationAnalysisProps } from './index';
import { InvoiceRecommendationAnalysis } from './index';

type InvoiceConfig = InvoiceConfigInfoInvoiceConfigSchema | null;
type PredefinedFieldEntry = { active: boolean; desc: string; [key: string]: unknown };
type PredefinedFieldsMap = Record<string, PredefinedFieldEntry | undefined>;

interface ConfiguredInvoiceRecommendationAnalysisProps extends InvoiceRecommendationAnalysisProps {
  invoiceConfig: InvoiceConfig;
  /**
   * When invoice-summary granularity is on AND no rows have been fetched / queued,
   * seed a single empty row so the user has somewhere to fill in. Used by the
   * document-invoice analysis flow (fresh invoice for one document); the DA-screen
   * flow leaves it off because that screen drives row creation via an explicit
   * "Add Invoice" button.
   */
  seedInitialEmptyInvoiceRow?: boolean;
}

const INVOICE_FIELD_COLUMN_IDS: LineItemColumnId[] = [
  'document',
  'item_date',
  'description',
  'amount',
  'amount_currency',
  'third_party_amount',
];

/**
 * Wrapper around InvoiceRecommendationAnalysis that applies InvoiceConfig-driven behavior:
 * - Gates invoice field columns by predefined_fields.active
 * - Adds third_party_amount column when active
 * - Applies label overrides from predefined_fields.desc
 * - Switches title and add-item behavior based on entity_granularity
 *
 * Callers pass the full column list (including assessment fields from DA type config).
 * This wrapper filters/augments the invoice field subset based on invoice config.
 */
export const ConfiguredInvoiceRecommendationAnalysis: React.FC<ConfiguredInvoiceRecommendationAnalysisProps> = ({
  invoiceConfig,
  lineItemColumnsToShow,
  columnLabelOverrides,
  lineItems,
  extraPendingRows,
  seedInitialEmptyInvoiceRow = false,
  ...rest
}) => {
  const ipf = invoiceConfig?.predefined_fields as PredefinedFieldsMap | undefined;
  const isInvoiceGranularity = invoiceConfig?.entity_granularity === 'invoice';

  // Filter invoice field columns by predefined_fields.active, add third_party_amount if active
  const adjustedColumns = useMemo((): LineItemColumnId[] | undefined => {
    if (!lineItemColumnsToShow) return undefined;
    if (!ipf) return lineItemColumnsToShow;

    let cols = lineItemColumnsToShow.filter((col) => {
      if (!INVOICE_FIELD_COLUMN_IDS.includes(col)) return true;
      if (col === 'third_party_amount') return ipf.third_party_amount?.active === true;
      return ipf[col]?.active !== false;
    });

    // Add third_party_amount if active and not already in the list
    if (ipf.third_party_amount?.active === true && !cols.includes('third_party_amount')) {
      const amountIdx = cols.indexOf('amount');
      if (amountIdx !== -1) {
        cols = [...cols.slice(0, amountIdx + 1), 'third_party_amount', ...cols.slice(amountIdx + 1)];
      } else {
        cols.push('third_party_amount');
      }
    }

    return cols;
  }, [lineItemColumnsToShow, ipf]);

  // Merge invoice field label overrides from config
  const adjustedLabelOverrides = useMemo((): Partial<Record<string, string>> | undefined => {
    if (!ipf) return columnLabelOverrides;
    const invoiceOverrides: Record<string, string> = {};
    for (const [key, field] of Object.entries(ipf)) {
      if (field?.desc) {
        invoiceOverrides[key] = field.desc;
      }
    }
    return { ...invoiceOverrides, ...columnLabelOverrides };
  }, [ipf, columnLabelOverrides]);

  // Invoice granularity: title, no add (use Add Invoice button). Empty state can either
  // show just the headers (DA-screen flow — explicit "Add Invoice" button drives row
  // creation) or seed a single empty row (document-invoice flow — fresh invoice with
  // nothing to fill in yet). Controlled by `seedInitialEmptyInvoiceRow`.
  // Trash visibility per row is handled inside the actions column (saved rows hide trash
  // to avoid orphaning an invoice; new rows keep it so the user can drop a row before save).
  const sectionTitle = isInvoiceGranularity ? 'Invoice Summary' : undefined;
  const showAddItem = isInvoiceGranularity ? false : rest.shouldShowAddItemButton;

  const finalLineItems = useMemo(() => {
    if (!isInvoiceGranularity || !seedInitialEmptyInvoiceRow) return lineItems;
    const hasFetchedRows = lineItems && lineItems.length > 0;
    const hasPendingRows = extraPendingRows && extraPendingRows.length > 0;
    if (hasFetchedRows || hasPendingRows) return lineItems;
    return [{ id: 'invoice_summary_seed' }];
  }, [isInvoiceGranularity, seedInitialEmptyInvoiceRow, lineItems, extraPendingRows]);

  return (
    <InvoiceRecommendationAnalysis
      {...rest}
      lineItems={finalLineItems}
      extraPendingRows={extraPendingRows}
      lineItemColumnsToShow={adjustedColumns}
      columnLabelOverrides={adjustedLabelOverrides}
      lineItemsSectionTitle={sectionTitle}
      isInvoiceGranularity={isInvoiceGranularity}
      shouldShowAddItemButton={showAddItem}
    />
  );
};
